import type { MergeTag } from "./config";
import { findOpenTagEnd, getTagAttrValue } from "./html-scan";

// --- Syntax Presets ---

export interface SyntaxPreset {
  value: RegExp;
  logic: RegExp;
}

export type SyntaxPresetName =
  "liquid" | "handlebars" | "mailchimp" | "ampscript";

export const SYNTAX_PRESETS: Record<SyntaxPresetName, SyntaxPreset> = {
  liquid: { value: /\{\{.+?\}\}/g, logic: /\{%-?\s*(\w+).*?-?%\}/g },
  handlebars: {
    value: /\{\{\{?.+?\}?\}\}/g,
    logic: /\{\{[#/](\w+).*?\}\}/g,
  },
  mailchimp: { value: /\*\|\w+\|\*/g, logic: /\*\|(\w+)[:|].*?\|\*/g },
  ampscript: { value: /%%=.+?=%%/g, logic: /%%\[\s*(\w+).*?\]%%/g },
};

const SYNTAX_TRIGGER_CHARS: Record<SyntaxPresetName, string> = {
  liquid: "{{",
  handlebars: "{{",
  mailchimp: "*|",
  ampscript: "%%=",
};

const SYNTAX_CLOSING_CHARS: Record<SyntaxPresetName, string> = {
  liquid: "}}",
  handlebars: "}}",
  mailchimp: "|*",
  ampscript: "=%%",
};

/**
 * Resolves the autocomplete trigger string for a syntax preset.
 * Returns null when the syntax doesn't match any built-in preset
 * (custom regex syntax — autocomplete cannot be enabled safely).
 */
export function getSyntaxTriggerChar(syntax: SyntaxPreset): string | null {
  for (const name of Object.keys(SYNTAX_PRESETS) as SyntaxPresetName[]) {
    if (SYNTAX_PRESETS[name].value.source === syntax.value.source) {
      return SYNTAX_TRIGGER_CHARS[name];
    }
  }
  return null;
}

/**
 * Resolves the closing delimiter for a syntax preset (e.g. `}}` for liquid,
 * `|*` for mailchimp). The autocomplete trigger detector uses it to tell an
 * open tag (`{{first`) from a completed one (`{{first_name}}`) so the popup
 * doesn't reappear over a finished tag. Returns null for custom syntaxes —
 * the same set for which `getSyntaxTriggerChar` returns null.
 */
export function getSyntaxClosingChar(syntax: SyntaxPreset): string | null {
  for (const name of Object.keys(SYNTAX_PRESETS) as SyntaxPresetName[]) {
    if (SYNTAX_PRESETS[name].value.source === syntax.value.source) {
      return SYNTAX_CLOSING_CHARS[name];
    }
  }
  return null;
}

export function resolveSyntax(
  syntax?: SyntaxPresetName | SyntaxPreset,
): SyntaxPreset {
  if (!syntax) {
    return SYNTAX_PRESETS.liquid;
  }

  if (typeof syntax === "string") {
    return SYNTAX_PRESETS[syntax] ?? SYNTAX_PRESETS.liquid;
  }

  return syntax;
}

// --- Merge Tag Utilities ---

function anchoredRegex(pattern: RegExp): RegExp {
  const source = pattern.source;
  const flags = pattern.flags.replace("g", "");
  return new RegExp(`^${source}$`, flags);
}

export function isMergeTagValue(value: string, syntax: SyntaxPreset): boolean {
  const trimmed = value?.trim() || "";
  // Handlebars (and similar) value regex is liberal enough to also match
  // logic tags like `{{#each items}}`. Exclude logic-shaped tags so callers
  // that rely on this discriminator (UI segmentation, label rendering)
  // don't misclassify them.
  if (anchoredRegex(syntax.logic).test(trimmed)) {
    return false;
  }
  return anchoredRegex(syntax.value).test(trimmed);
}

export function getMergeTagLabel(value: string, mergeTags: MergeTag[]): string {
  const found = mergeTags.find((p) => p.value === value);
  if (found) {
    return found.label;
  }
  return value;
}

export function resolveHtmlMergeTagLabels(
  html: string,
  mergeTags: MergeTag[],
): string {
  return rewriteSpanByAttr(html, "data-merge-tag", (value) =>
    getMergeTagLabel(value, mergeTags),
  );
}

/**
 * The example value configured for `value`, or `undefined` when the tag is
 * unknown or declares none. Unlike `getMergeTagLabel`, which falls back to the
 * raw token, absence is reported as `undefined` so callers can choose their own
 * fallback — Sample mode falls back to the label, never to a raw token.
 */
export function getMergeTagSample(
  value: string,
  mergeTags: MergeTag[],
): string | undefined {
  return mergeTags.find((p) => p.value === value)?.sample;
}

/**
 * Rewrite rich-text HTML for the previews' Sample mode.
 *
 * A tag **with** a sample is replaced by that value as bare text, wrapper and
 * all — dropping the `<span>` is what removes the merge-tag highlight, so the
 * value reads as ordinary content. A tag **without** one keeps its span and
 * shows its label, so it stays visibly dynamic; that difference is deliberate,
 * and it doubles as a signal that a sample is still unset.
 *
 * The unwrapped output is inert — `data-merge-tag` is gone from it — so this
 * result must only ever reach a preview surface, never `getContent()`, an
 * export or a send.
 */
export function substituteHtmlMergeTagSamples(
  html: string,
  mergeTags: MergeTag[],
): string {
  return rewriteSpanByAttr(
    html,
    "data-merge-tag",
    (value) =>
      escapeTagText(
        getMergeTagSample(value, mergeTags) ??
          getMergeTagLabel(value, mergeTags),
      ),
    { unwrap: (value) => getMergeTagSample(value, mergeTags) !== undefined },
  );
}

/**
 * Whether any configured tag declares a `sample`.
 *
 * Gates the whole feature: with no samples anywhere the previews' Sample view
 * would be identical to the Label view, so the editor defaults to Label and
 * renders no toggle. That keeps the feature invisible until a consumer opts in.
 */
export function hasMergeTagSamples(mergeTags: MergeTag[]): boolean {
  return mergeTags.some((tag) => tag.sample !== undefined);
}

/**
 * Replace declared tokens with their sample values in a plain string — used for
 * values that reach no label-resolving component, namely `html` block content
 * and custom-block field values.
 *
 * Matching is on the exact configured `value`, so consumer text cannot
 * false-positive and `href="{{link}}"` resolves correctly. Tags without a
 * sample are left untouched, which is what keeps a missing sample visible.
 */
export function substituteTextMergeTagSamples(
  text: string,
  mergeTags: MergeTag[],
): string {
  if (!text) return text;

  let out = text;
  for (const tag of mergeTags) {
    if (tag.sample === undefined) continue;
    // split/join rather than a RegExp: the token is a literal, so this needs no
    // escaping of regex metacharacters (`{`, `|`, `*` all appear in real syntaxes).
    out = out.split(tag.value).join(tag.sample);
  }
  return out;
}

/**
 * Escape a sample before it is inlined into rich-text HTML. Samples are
 * consumer-authored strings, so `<`, `&` and quotes would otherwise alter the
 * surrounding markup.
 */
function escapeTagText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function containsMergeTag(value: string, syntax: SyntaxPreset): boolean {
  if (!value) return false;

  const valueRegex = new RegExp(syntax.value.source, syntax.value.flags);
  const logicRegex = new RegExp(syntax.logic.source, syntax.logic.flags);

  return valueRegex.test(value) || logicRegex.test(value);
}

export function isLogicMergeTagValue(
  value: string,
  syntax: SyntaxPreset,
): boolean {
  return anchoredRegex(syntax.logic).test(value?.trim() || "");
}

export function getLogicMergeTagKeyword(
  value: string,
  syntax: SyntaxPreset,
): string {
  const regex = new RegExp(
    syntax.logic.source,
    syntax.logic.flags.replace("g", ""),
  );
  const match = value.match(regex);
  return match && match[1] ? match[1].toUpperCase() : value;
}

export function resolveHtmlLogicMergeTagLabels(
  html: string,
  syntax: SyntaxPreset,
): string {
  return rewriteSpanByAttr(html, "data-logic-merge-tag", (value) =>
    getLogicMergeTagKeyword(value, syntax),
  );
}

/**
 * Walk `html` and rewrite the inner text of every `<span … {attrName}="…">…</span>`
 * by passing the attribute value through `relabel`. Linear in `html.length`:
 * each `indexOf` advances the cursor monotonically, and no regex backtracking
 * can run over the whole string.
 *
 * Replaces the original `/<span[^>]*…[^>]*>(.*?)<\/span>/g` pattern, which
 * was polynomial-ReDoS over inputs that contained many `<span` starts with
 * no closing `>`.
 *
 * Tag boundaries and attribute values are resolved by `findOpenTagEnd` /
 * `getTagAttrValue`, which are quote-aware. A merge-tag value may itself
 * contain `<` / `>` when the consumer configures a custom syntax — Smarty-style
 * `<% $email %>` — and those characters survive attribute serialization
 * verbatim, so anything that treats the first `>` as the tag end reads a
 * truncated attribute and resolves nothing.
 */
function rewriteSpanByAttr(
  html: string,
  attrName: string,
  relabel: (value: string) => string,
  options?: { unwrap?: (value: string) => boolean },
): string {
  let out = "";
  let i = 0;
  while (i < html.length) {
    const open = html.indexOf("<span", i);
    if (open === -1) {
      out += html.substring(i);
      break;
    }
    const afterTagName = html[open + 5];
    if (
      afterTagName !== ">" &&
      afterTagName !== " " &&
      afterTagName !== "\t" &&
      afterTagName !== "\n" &&
      afterTagName !== "\r" &&
      afterTagName !== "/"
    ) {
      out += html.substring(i, open + 5);
      i = open + 5;
      continue;
    }
    const openEnd = findOpenTagEnd(html, open + 5);
    if (openEnd === -1) {
      out += html.substring(i);
      break;
    }
    const closeStart = html.indexOf("</span>", openEnd + 1);
    if (closeStart === -1) {
      out += html.substring(i);
      break;
    }
    const attrs = html.substring(open + 5, openEnd);
    const value = getTagAttrValue(attrs, attrName);
    if (value === null) {
      // This `<span>` isn't the one we're looking for — emit up to and
      // including the `<span` literal and let the next iteration scan
      // inward. Skipping straight to the matching `</span>` would swallow
      // any nested merge-tag span in the same loop iteration.
      out += html.substring(i, open + 5);
      i = open + 5;
      continue;
    }
    const newLabel = relabel(value);
    if (options?.unwrap?.(value)) {
      // Emit the text before the span, then the replacement alone — the open
      // and close tags are dropped, so the span's styling (the merge-tag
      // highlight) no longer applies to it. Decided per tag, so a tag that
      // resolved to a sample loses the highlight while one that fell back to
      // its label keeps it.
      out += html.substring(i, open);
      out += newLabel;
    } else {
      out += html.substring(i, openEnd + 1);
      out += newLabel;
      out += "</span>";
    }
    i = closeStart + 7;
  }
  return out;
}
